import { Badge } from '@qodo/design-system';
import {
  type PlanSku,
  type PricingConfig,
} from 'hooks/billing/usePricingConfig';
import { Check } from 'lucide-react';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { estimateReviewsCount } from '../utils';
import { RadioDot } from './RadioDot';

const spawnPopup = spawn('billing.popup');

// Badge straddling the card's top border (recommended / current-plan label).
const SKU_CARD_BADGE_CLASS = 'absolute -top-2 left-4 bg-fill-panel-neutral!';

const SKU_CARD_SURFACE = {
  idle: 'border-border-interactive-neutral-idle bg-fill-interactive-neutral-idle hover:border-border-interactive-neutral-hover hover:bg-fill-interactive-neutral-hover',
  selected:
    'border-border-interactive-emphasis-active bg-fill-interactive-emphasis-idle',
  currentPlan:
    'border-border-panel-positive bg-fill-interactive-neutral-idle cursor-default',
} as const;

interface SkuCardProps {
  plan: PlanSku;
  cfg: PricingConfig;
  checked: boolean;
  /** Marks the usage-recommended SKU (PLT-4059) — renders the "Recommended" badge. */
  recommended?: boolean;
  /** Current active plan — renders a green check instead of a radio dot and blocks selection. */
  isCurrentPlan?: boolean;
  onSelect: (sku: string) => void;
}

/**
 * Single credit-package option in the SKU selector. Accessible radio
 * (`role="radio"` + `aria-checked`) so the row reads as a radiogroup and is
 * selectable by keyboard. States (idle / hover / selected / current-plan) mirror
 * the Figma spec (node 3596:12380).
 */
export const SkuCard = ({
  plan,
  cfg,
  checked,
  recommended = false,
  isCurrentPlan = false,
  onSelect,
}: SkuCardProps) => {
  const surfaceClass = isCurrentPlan
    ? SKU_CARD_SURFACE.currentPlan
    : checked
      ? SKU_CARD_SURFACE.selected
      : SKU_CARD_SURFACE.idle;

  const cardClassName = cn(
    'relative flex min-w-[230px] max-w-fit-content shrink-0 items-start justify-between gap-2 rounded-lg border pl-6 py-4 pr-5 transition-colors',
    !isCurrentPlan && 'cursor-pointer',
    surfaceClass,
  );

  const metaClassName = cn(
    'text-xs',
    checked
      ? 'text-text-interactive-emphasis-idle'
      : 'text-general-neutral-subtle',
  );

  const onCardClick = () => {
    if (!isCurrentPlan) onSelect(plan.sku);
  };

  const onCardKeyDown = (e: React.KeyboardEvent) => {
    if (!isCurrentPlan && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onSelect(plan.sku);
    }
  };

  return (
    <div
      role="radio"
      aria-checked={checked}
      aria-disabled={isCurrentPlan}
      tabIndex={isCurrentPlan ? -1 : 0}
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
      className={cardClassName}
    >
      {isCurrentPlan && (
        <Badge variant="success" className={SKU_CARD_BADGE_CLASS}>
          {spawnPopup('sku.active-plan')}
        </Badge>
      )}
      {recommended && !isCurrentPlan && (
        <Badge className={SKU_CARD_BADGE_CLASS}>
          {spawnPopup('sku.recommended')}
        </Badge>
      )}
      <div className="flex flex-col gap-1">
        <p className="text-xl font-medium text-text-panel-neutral-strong">
          {spawnPopup('sku.reviews', {
            count: String(estimateReviewsCount(plan.credits, cfg)),
          })}
        </p>
        <p className={metaClassName}>
          {spawnPopup('sku.credits', {
            credits: plan.credits.toLocaleString(),
          })}
        </p>
      </div>
      {isCurrentPlan ? (
        <Check className="size-4 shrink-0 text-text-general-positive-strong" />
      ) : (
        <RadioDot checked={checked} />
      )}
    </div>
  );
};
